import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { ExpertBrowser } from "./ExpertBrowser";
import { useExpertSelection } from "./ExpertSelectionContext";

type AvailableExpertDropdownProps = React.PropsWithChildren<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteExpertClick: () => void;
  disabled?: boolean;
}>;

export const AvailableExpertDropdown = ({
  children,
  open,
  onOpenChange,
  onInviteExpertClick,
  disabled,
}: AvailableExpertDropdownProps) => {
  const { selectedExpertUuids, selectExperts, enableInviteDialog, max } =
    useExpertSelection();

  const handleOpenChange = (isOpen: boolean) => {
    if (!disabled) {
      onOpenChange(isOpen);
    }
  };

  const handleOnInviteExpertClick = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    onOpenChange(false);
    onInviteExpertClick();
  };

  const handleExpertSelection = (selectedExpertUuids: Set<string>) => {
    selectExperts(selectedExpertUuids);
    if (max === 1) {
      onOpenChange(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        className="flex w-160 max-w-180 min-w-140 flex-1 flex-col"
      >
        <DropdownMenuLabel className="text-md flex flex-row items-center gap-4 p-2 font-semibold">
          {(max === 1 || selectedExpertUuids.size === 0) && "Select an Expert"}
          {max > 1 && selectedExpertUuids.size === 1 && "Selected 1 Expert"}
          {max > 1 &&
            selectedExpertUuids.size > 1 &&
            `Selected ${selectedExpertUuids.size} of ${max} Experts`}
        </DropdownMenuLabel>
        <DropdownMenuGroup className="flex flex-1 flex-col overflow-hidden px-2">
          <ExpertBrowser
            source="availableExperts"
            variant="list"
            selectedExpertUuids={selectedExpertUuids}
            previousSelectedExpertUuids={null}
            onExpertSelection={handleExpertSelection}
            max={max}
          />
        </DropdownMenuGroup>
        <DropdownMenuGroup className="flex flex-row items-center overflow-hidden px-2 py-4">
          {enableInviteDialog && (
            <Button
              onClick={(e) => handleOnInviteExpertClick(e)}
              variant="outline"
              size="sm"
            >
              <FontAwesomeIcon icon={faPlus} /> Invite Experts
            </Button>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
